import React, { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import {
  Box, TextField, Switch, Tooltip, IconButton, Typography,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

const ITEM_TYPE = 'BULLET_FIELD';

/**
 * One row in the bullet-field list. Draggable to reorder.
 * Props:
 *   index      — position in array
 *   field      — BulletFieldDef
 *   onChange   — (index, updatedField) => void
 *   onRemove   — (index) => void
 *   onMove     — (fromIndex, toIndex) => void
 */
function BulletFieldRow({ index, field, onChange, onRemove, onMove }) {
  const ref = useRef(null);

  const [{ isDragging }, drag] = useDrag({
    type: ITEM_TYPE,
    item: { index },
    collect: (m) => ({ isDragging: m.isDragging() }),
  });

  const [, drop] = useDrop({
    accept: ITEM_TYPE,
    hover(item) {
      if (item.index === index) return;
      onMove(item.index, index);
      item.index = index;
    },
  });

  drag(drop(ref));

  const specialLabel = field.specialType
    ? ` [${field.specialType}]`
    : '';

  return (
    <Box
      ref={ref}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        py: 0.75,
        px: 1,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        mb: 0.75,
        opacity: isDragging ? 0.4 : 1,
        bgcolor: field.enabled ? 'background.paper' : 'action.disabledBackground',
        cursor: 'grab',
      }}
    >
      <DragIndicatorIcon sx={{ color: 'grey.400', fontSize: 18, flexShrink: 0 }} />

      <Box sx={{ flex: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          label="Field"
          value={field.fieldPath || ''}
          onChange={(e) => onChange(index, { ...field, fieldPath: e.target.value })}
          sx={{ minWidth: 160 }}
          helperText={specialLabel || undefined}
          FormHelperTextProps={{ sx: { m: 0, fontSize: '0.7rem', color: 'primary.main' } }}
        />
        {!field.specialType && (
          <TextField
            size="small"
            label="Format (use <value>)"
            value={field.format || ''}
            onChange={(e) => onChange(index, { ...field, format: e.target.value || null })}
            placeholder="e.g. <value> Exterior"
            sx={{ minWidth: 200 }}
          />
        )}
      </Box>

      <Tooltip title={field.enabled ? 'Enabled' : 'Disabled'}>
        <Switch
          size="small"
          checked={field.enabled !== false}
          onChange={(e) => onChange(index, { ...field, enabled: e.target.checked })}
        />
      </Tooltip>

      <Tooltip title="Remove field">
        <IconButton size="small" onClick={() => onRemove(index)} color="error">
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

/**
 * Full list of bullet fields, draggable to reorder.
 * Props:
 *   fields     — BulletFieldDef[]
 *   onChange   — (newFields: BulletFieldDef[]) => void
 */
export default function BulletFieldEditor({ fields = [], onChange }) {
  const handleMove = (from, to) => {
    const next = [...fields];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
  };

  const handleChange = (index, updated) => {
    const next = [...fields];
    next[index] = updated;
    onChange(next);
  };

  const handleRemove = (index) => {
    onChange(fields.filter((_, i) => i !== index));
  };

  const handleAdd = () => {
    onChange([
      ...fields,
      { id: `custom_${Date.now()}`, fieldPath: '', format: null, specialType: null, candidates: [], enabled: true },
    ]);
  };

  return (
    <Box>
      {fields.map((field, index) => (
        <BulletFieldRow
          key={field.id || index}
          index={index}
          field={field}
          onChange={handleChange}
          onRemove={handleRemove}
          onMove={handleMove}
        />
      ))}

      <Box
        onClick={handleAdd}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: 0.75,
          px: 1,
          borderRadius: 1,
          border: '1px dashed',
          borderColor: 'primary.main',
          color: 'primary.main',
          cursor: 'pointer',
          mt: 0.5,
          '&:hover': { bgcolor: 'primary.50' },
        }}
      >
        <Typography variant="body2">+ Add Field</Typography>
      </Box>
    </Box>
  );
}
